import { useState, useEffect } from 'react'
import { Container } from '@uswitch/ev-core/react'
import ProductTable from './elements'
import { Button } from '../../elements/trustyle-button'
import DropDown from '../../elements/drop-down'
import { TruMoneyFilter, TruSort } from '@uswitch/spark-icons/tru'
import SlideReveal from '../../elements/slide-reveal'
import { getValue, getField, getDisabledValue } from './utils'
import Image from '../../elements/imgix-img'

import Addons from './addons'
import Cell from './cell'
import Badges from './badges'
import Calculator from './calculator'
import Filter from './filters'
import LoadingState from '../loading-state'

const PT = ({
  data = [],
  header = {},
  grid = { contents: [] },
  addons,
  sorts = {},
  filters = [],
  maxPerPage,
  calculator = {},
  totalHits,
  offset = 0,
  extras,
  onSort,
  onFilter,
  onPagination,
  onCalculate,
  calcModalContent,
  clickableRow,
  loadingLabel,
  settings = {},
  calculatorInputs = {},
  onClickEligibility,
  card = false,
}) => {
  const { isAlwaysVisible: isCalculatorAlwaysOpen } = calculator || {}
  const sortOptions =
    sorts && sorts.selectable && sorts.selectable.length
      ? [
          { value: '', text: 'Sort by' },
          ...sorts.selectable.map(sort => {
            return { value: getField(sort.value), text: sort.label }
          }),
        ]
      : []

  const [isLoading, setIsLoading] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [calculatorOpen, setCalculatorOpen] = useState(!!isCalculatorAlwaysOpen)
  const [filterBy, setFilterBy] = useState(() => {
    if (!filters) {
      return []
    }
    return filters.map(filter => ({
      label: filter.label,
      type: filter.type,
      options: filter.options.map(option => ({
        label: option.label,
        value: filter.type === 'dropdown' ? filter.options.indexOf(option) === 0 : false,
      })),
    }))
  })

  const [pageOffset, setPageOffset] = useState(offset)

  const defaultSort = sorts.selectable?.find(sort => sort.default) || {}
  const [sortBy, setSortByState] = useState(getField(defaultSort.value) || '')

  useEffect(() => {
    setPageOffset(offset)
  }, [offset])

  const setSortBy = sortBy => {
    if (!sortBy) return
    setSortByState(sortBy)

    if (typeof onSort === 'function' && sortBy) {
      const sortByObj = getSortByObj(sortBy)
      if (sortByObj) {
        setIsLoading(true)
        const maybePromise = onSort(sortByObj)
        Promise.resolve(maybePromise).then(() => {
          setPageOffset(0)
          setIsLoading(false)
        })
      }
    }
  }

  const getSortByObj = sortBy => sorts.selectable?.find(({ value }) => getField(value) === sortBy)

  const handleFilterClick = () => {
    setIsLoading(true)
    const maybePromise = onFilter(filterBy)
    Promise.resolve(maybePromise).then(() => {
      setFilterOpen(false)
      setPageOffset(0)
      setIsLoading(false)
    })
  }

  const handlePaginationClick = () => {
    setIsLoading(true)
    const maybePromise = onPagination(pageOffset)
    Promise.resolve(maybePromise).then(() => {
      setPageOffset(pageOffset + maxPerPage)
      setIsLoading(false)
    })
  }

  const handleCalculatorClick = data => {
    setCalculatorOpen(!calculatorOpen)
    setIsLoading(true)
    const maybePromise = onCalculate(data)
    Promise.resolve(maybePromise).then(() => {
      setCalculatorOpen(false)
      setPageOffset(0)
      setIsLoading(false)
    })
  }

  const handleFilterButton = () => {
    setCalculatorOpen(false)
    setFilterOpen(!filterOpen)
  }

  const handleCalculatorButton = () => {
    setFilterOpen(false)
    setCalculatorOpen(!calculatorOpen)
  }

  /** =============
   * Mortgage-specific code
   * We need to display LTV for mortgages in the compliance text, and the only way to do it is to plug it here.
   * This code also allows to refer calculator field values in the compliance text if needed
   */
  let ltv = 'NONE'
  const propertyValue = parseInt(calculatorInputs.propertyValue, 10)
  const mortgageAmount = parseInt(calculatorInputs.mortgageAmount, 10)
  if (propertyValue && mortgageAmount) {
    ltv = `${Math.floor((mortgageAmount / propertyValue) * 100)}%`
  }
  const calculatorReplaceFields = {
    ...calculatorInputs,
    ltv,
  }
  // ==============
  const replaceObject = {
    resultCount: totalHits,
    resultOrResults: totalHits === 1 ? 'result' : 'results',
    sortBy: getSortByObj(sortBy)?.label.toLowerCase() || sortBy,
    complianceLink: extras?.complianceLink,
    ...calculatorReplaceFields,
  }

  const complianceText =
    extras &&
    extras.complianceText &&
    extras.complianceText.replace(/\$\{([a-z]+)\}/gi, (original, match) => {
      return match in replaceObject ? replaceObject[match] : original
    })

  const calculatorButtonColor = calculatorOpen ? 'grey-05' : 'none'
  const loadingStateBackgroundColour = isLoading ? 'opaque-plum-01' : 'inherit'

  return (
    <Container>
      <If
        condition={
          complianceText || (filterBy.length && onFilter) || (sortOptions.length && onSort)
        }
      >
        <div
          sx={{
            display: [null, 'flex'],
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingBottom: 9,
            variant: 'compounds.product-table.variants.ordering.second',
          }}
        >
          <If condition={complianceText}>
            <div
              sx={{
                marginRight: 'auto',
                fontSize: 'xs',
              }}
              dangerouslySetInnerHTML={{ __html: complianceText }}
            ></div>
          </If>
          <If
            condition={
              (filterBy.length && onFilter) ||
              (sortOptions.length && onSort) ||
              (calculator && onCalculate && calculator.steps)
            }
          >
            <div
              sx={{
                display: 'flex',
                flexShrink: 0,
                marginLeft: [null, 'md'],
                marginTop: ['sm', 0],
              }}
            >
              <If condition={filterBy.length && onFilter}>
                <div
                  sx={{
                    display: 'inline-block',
                    marginRight: sortOptions.length && onSort ? 'sm' : null,
                  }}
                >
                  <Button
                    variant='control'
                    sx={{ fontSize: 'xs', whiteSpace: 'nowrap' }}
                    onClick={() => handleFilterButton()}
                  >
                    <div sx={{ display: 'inline-block', width: '1em', marginRight: 'xs' }}>
                      <TruMoneyFilter color='fuschia' />
                    </div>
                    Filter
                  </Button>
                </div>
              </If>
              <If
                condition={!isCalculatorAlwaysOpen && calculator && onCalculate && calculator.steps}
              >
                <div
                  sx={{
                    display: 'inline-block',
                    marginRight: sortOptions.length && onSort ? 'sm' : null,
                  }}
                >
                  <Button
                    variant='control'
                    sx={{
                      fontSize: 'xs',
                      whiteSpace: 'nowrap',
                      backgroundColor: calculatorButtonColor,
                    }}
                    onClick={() => handleCalculatorButton()}
                  >
                    <div sx={{ display: 'inline-block', width: '1em', marginRight: 'xs' }}>
                      <TruMoneyFilter color='fuschia' />
                    </div>
                    Change loan amount
                  </Button>
                </div>
              </If>
              <If condition={sortOptions.length && onSort}>
                <div sx={{ display: 'inline-block' }}>
                  <DropDown
                    name='sorts'
                    onBlur={() => {}}
                    onChange={val => setSortBy(val)}
                    options={sortOptions}
                    value={sortBy}
                    overlay={
                      <span>
                        <div sx={{ display: 'inline-block', width: '1em', marginRight: 'xs' }}>
                          <TruSort color='fuschia' />
                        </div>
                        Sort by
                      </span>
                    }
                    sx={{ borderColor: 'grey-20', fontSize: 'xs' }}
                  />
                </div>
              </If>
            </div>
          </If>
        </div>
      </If>
      <If condition={filterBy.length && onFilter}>
        <SlideReveal open={filterOpen}>
          <Filter
            filterBy={filterBy}
            setFilterBy={setFilterBy}
            handleFilterClick={handleFilterClick}
            setFilterOpen={setFilterOpen}
          />
        </SlideReveal>
      </If>
      <If condition={calculator && onCalculate && calculator.steps}>
        <div
          sx={{
            variant: 'compounds.product-table.variants.ordering.first',
          }}
        >
          <SlideReveal open={calculatorOpen}>
            <Calculator
              calculator={calculator}
              submit={handleCalculatorClick}
              modalContent={calcModalContent}
            />
          </SlideReveal>
        </div>
      </If>
      <If condition={isLoading}>
        <LoadingState label={loadingLabel} />
      </If>
      <div sx={{ position: 'relative' }}>
        <If condition={isLoading}>
          <div
            sx={{
              position: 'absolute',
              backgroundColor: loadingStateBackgroundColour,
              zIndex: '1',
              width: '100%',
              height: '100%',
            }}
          ></div>
        </If>
        <For each='product' of={data} index='id'>
          <ProductTable.Row
            key={product.id}
            badges={Badges(product)}
            preTitle={getValue(header.kicker, product)}
            rowTitle={getValue(header.title, product)}
            subtitle={getValue(header.subtitle, product)}
            image={
              header.image && (
                <ProductTable.cells.HeaderImage key={`${product.id}-img`}>
                  <Image
                    src={getValue(header.image, product)}
                    alt={getValue(header.alt, product)}
                  />
                </ProductTable.cells.HeaderImage>
              )
            }
            addons={Addons(addons, product, onClickEligibility)}
            clickableRow={getValue(clickableRow, product)}
            disabled={getDisabledValue(settings.disablingField, product)}
            card={card}
          >
            <For each='cell' of={grid.contents} index='key'>
              {Cell(cell.type, { key, cell, data: product, headerImage: header.image })}
            </For>
          </ProductTable.Row>
        </For>
      </div>
      <If condition={totalHits > maxPerPage && totalHits > pageOffset && maxPerPage !== 0}>
        <div sx={{ display: 'flex', justifyContent: 'center', marginTop: 'md' }}>
          <Button variant='primary' onClick={handlePaginationClick} disabled={isLoading}>
            {isLoading ? 'Loading' : `Load ${maxPerPage} More`}
          </Button>
        </div>
      </If>
    </Container>
  )
}

export default PT
